import type { SearchResponse } from '@algolia/client-search'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import { buildAlgoliaAnalyticsTags } from '~/utils/searchEngineHelpers'
import { useSearchClient } from '~/composables/search/useSearchClient'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { typesenseSearch } from '~/utils/typesenseClient'
import { adaptTypesenseSearchResponse } from '~/utils/adaptTypesenseSearchResponse'
import {
  priceAmountCents,
  buildKitsTypesenseFilterBy,
} from '~/utils/kitsTypesenseFilters'

export const KITS_BROWSE_PER_PAGE = 20

/** Typesense requires searchable fields per request; kits browse never sends
 * free text (q is always '*'), so these just need to be valid indexed string
 * fields on the products collection. */
const KITS_TYPESENSE_QUERY_BY = 'title,categories.name,supplier'

/** When a parent (e.g. kits/[category]) provides browse state for schema + showcase */
export const KITS_BROWSE_PRODUCTS_KEY = Symbol('kitsBrowseProducts')

interface UseKitsBrowseProductsOptions {
  /**
   * Strapi `categorySlug` → Algolia `categories.slug`.
   * Do not pass `route.params.category` (kits page permalink); it often differs, e.g. `/kits/malset` vs `malkurs`.
   */
  initialCategorySlug?: string | null
}

function buildPriceFilterString(
  priceMinEur: number | undefined,
  priceMaxEur: number | undefined
): string | undefined {
  const filterParts: string[] = []
  if (priceMinEur != null) {
    filterParts.push(
      `(default_price.amount >= ${priceAmountCents(priceMinEur)})`
    )
  }
  if (priceMaxEur != null) {
    filterParts.push(
      `(default_price.amount <= ${priceAmountCents(priceMaxEur)})`
    )
  }
  if (filterParts.length === 0) return undefined
  return filterParts.join(' AND ')
}

function parseKitsBrowsePageFromRoute(route: ReturnType<typeof useRoute>) {
  const raw = route.query.page
  const s = Array.isArray(raw) ? raw[0] : raw
  if (s == null || s === '') return 1
  const n = Number(s)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1
}

export const useKitsBrowseProducts = (
  options: UseKitsBrowseProductsOptions = {}
) => {
  const { locale } = useI18n()
  const {
    public: { appEnv },
  } = useRuntimeConfig()
  const route = useRoute()
  // In the cache key so an A/B engine force after hydration refetches (KFT-4115).
  const searchEngine = useSearchEngine()

  const page = ref(parseKitsBrowsePageFromRoute(route))

  watch(
    () => route.query.page,
    () => {
      const next = parseKitsBrowsePageFromRoute(route)
      if (page.value !== next) {
        page.value = next
      }
    }
  )

  const categorySlug = ref<string | null>(options.initialCategorySlug ?? null)
  const saleOnly = ref(false)
  const priceMinEur = ref<number | undefined>()
  const priceMaxEur = ref<number | undefined>()

  /** Per filter state — avoids sharing one payload across navigations with mismatched refs */
  const kitsBrowseAsyncKey = computed(
    () =>
      `kits-browse-${searchEngine.value}:${locale.value}:${categorySlug.value ?? 'all'}:p${page.value}:sale${saleOnly.value ? 1 : 0}:min${priceMinEur.value ?? 'x'}:max${priceMaxEur.value ?? 'x'}`
  )

  const { data, pending, error, refresh } = useAsyncData(
    kitsBrowseAsyncKey,
    async (): Promise<{ results: SearchResponse<SearchEngineEventItem>[] }> => {
      const handle = useSearchClient()

      if (handle.engine === 'typesense') {
        // `has_promotions` isn't a facet-enabled field on the live products_<locale>
        // collection (confirmed against the cluster — requesting facet_by on it 404s
        // the whole search), so we can't get per-value counts the way Algolia does.
        // `hasSaleItemsInScope` falls back to its nbHits-based heuristic when
        // `facets` is empty, which is what happens here.
        const typesenseResult = await typesenseSearch<SearchEngineEventItem>(
          {
            collection: getBoxesIndexName(locale.value),
            q: '*',
            query_by: KITS_TYPESENSE_QUERY_BY,
            page: page.value,
            per_page: KITS_BROWSE_PER_PAGE,
            filter_by: buildKitsTypesenseFilterBy({
              categorySlug: categorySlug.value,
              saleOnly: saleOnly.value,
              priceMinEur: priceMinEur.value,
              priceMaxEur: priceMaxEur.value,
            }),
          },
          // No per-viewer params: same request for everyone on this filter state.
          { ...handle, useCache: true }
        )

        const normalized = adaptTypesenseSearchResponse<SearchEngineEventItem>(
          typesenseResult,
          { query: '', perPage: KITS_BROWSE_PER_PAGE }
        )

        return {
          results: [
            normalized as unknown as SearchResponse<SearchEngineEventItem>,
          ],
        }
      }

      const filterParts: string[] = []
      if (saleOnly.value) {
        filterParts.push('(has_promotions:true)')
      }
      const priceFilters = buildPriceFilterString(
        priceMinEur.value,
        priceMaxEur.value
      )
      if (priceFilters) {
        filterParts.push(priceFilters)
      }

      /** Narrow by Algolia category slug when user selects a Strapi chip */
      const categoryFacetFilters = categorySlug.value
        ? ([`categories.slug:${categorySlug.value}`] as string[])
        : undefined

      const mainQuery = {
        indexName: getBoxesIndexName(locale.value),
        hitsPerPage: KITS_BROWSE_PER_PAGE,
        page: page.value - 1,
        clickAnalytics: true,
        analyticsTags: buildAlgoliaAnalyticsTags(appEnv),
        facets: ['has_promotions'],
        maxValuesPerFacet: 5,
        ...(categoryFacetFilters ? { facetFilters: categoryFacetFilters } : {}),
        ...(filterParts.length > 0
          ? { filters: filterParts.join(' AND ') }
          : {}),
      }

      return handle.client.search<SearchEngineEventItem>([mainQuery])
    },
    {
      watch: [page, categorySlug, saleOnly, priceMinEur, priceMaxEur, locale],
    }
  )

  watch(
    [categorySlug, saleOnly, priceMinEur, priceMaxEur, locale],
    () => {
      page.value = 1
    },
    { flush: 'sync' }
  )

  const firstResult = computed(
    () =>
      data.value?.results?.[0] as
        | SearchResponse<SearchEngineEventItem>
        | undefined
  )

  const hasSaleItemsInScope = computed(() => {
    const res = firstResult.value
    if (!res) return true
    const fp = res.facets?.has_promotions as Record<string, number> | undefined
    if (fp != null) return (fp['true'] ?? 0) > 0
    return (res.nbHits ?? 0) === 0 ? false : true
  })

  watch(hasSaleItemsInScope, (has) => {
    if (!has && saleOnly.value) {
      saleOnly.value = false
    }
  })

  const products = computed(() => firstResult.value?.hits ?? [])

  const totalHits = computed(() => firstResult.value?.nbHits ?? 0)

  const totalPages = computed(() => {
    const nb = firstResult.value?.nbPages
    if (nb == null || nb < 1) return 1
    return nb
  })

  return {
    products,
    totalHits,
    totalPages,
    pending,
    error,
    refresh,
    page,
    perPage: KITS_BROWSE_PER_PAGE,
    categorySlug,
    saleOnly,
    priceMinEur,
    priceMaxEur,
    hasSaleItemsInScope,
  }
}

export type KitsBrowseProductsReturn = ReturnType<typeof useKitsBrowseProducts>
